import React, { useEffect, useRef, useState } from 'react';
import { Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import useScrollFadeIn from '../hooks/useScrollFadeIn';
import { testimonials } from '../data/testimonials';

const AUTO_ADVANCE_MS = 7000;
const SWIPE_THRESHOLD = 50;

export default function Testimonials() {
  const { ref, isVisible } = useScrollFadeIn();
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const touchStartX = useRef(null);

  const total = testimonials.length;

  const next = () => setActiveIndex((i) => (i + 1) % total);
  const prev = () => setActiveIndex((i) => (i - 1 + total) % total);

  useEffect(() => {
    if (paused || total <= 1) return;
    intervalRef.current = setInterval(() => {
      setActiveIndex((i) => (i + 1) % total);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(intervalRef.current);
  }, [paused, total]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') prev();
    else if (e.key === 'ArrowRight') next();
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > SWIPE_THRESHOLD) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  if (total === 0) return null;

  const active = testimonials[activeIndex];

  return (
    <section
      id="testimonials"
      className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8"
      aria-label="Customer testimonials"
    >
      <div
        ref={ref}
        className={`max-w-3xl mx-auto transition-all duration-700 ease-out ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <h2
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3"
          style={{ color: 'var(--text-heading)' }}
        >
          What early adopters are saying
        </h2>
        <p
          className="text-sm sm:text-base text-center mb-10"
          style={{ color: 'var(--text-muted)' }}
        >
          Real businesses replying faster, capturing more leads, and freeing up their teams.
        </p>

        <div
          className="relative rounded-3xl p-8 sm:p-10"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border-card)',
            boxShadow: 'var(--shadow-card-lg)',
            backdropFilter: 'blur(var(--glass-blur))',
            WebkitBackdropFilter: 'blur(var(--glass-blur))',
          }}
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onKeyDown={onKeyDown}
          tabIndex={0}
          role="region"
          aria-roledescription="carousel"
          aria-live="polite"
        >
          <Quote
            className="w-8 h-8 mb-4"
            style={{ color: 'var(--text-accent)', opacity: 0.5 }}
          />

          <blockquote
            className="text-lg sm:text-xl leading-relaxed mb-6"
            style={{ color: 'var(--text-heading)' }}
          >
            "{active.quote}"
          </blockquote>

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--text-heading)' }}>
                {active.author}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {active.role} · {active.company}
              </p>
            </div>
            {active.metric && (
              <span
                className="self-start sm:self-auto inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#fff',
                }}
              >
                {active.metric}
              </span>
            )}
          </div>

          {total > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous testimonial"
                className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  color: 'var(--text-accent)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={next}
                aria-label="Next testimonial"
                className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 w-9 h-9 items-center justify-center rounded-full transition-all duration-200 hover:scale-110"
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border-card)',
                  color: 'var(--text-accent)',
                  boxShadow: 'var(--shadow-card)',
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {total > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((t, i) => (
              <button
                key={t.id}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to testimonial ${i + 1}`}
                aria-current={i === activeIndex}
                className="rounded-full transition-all duration-200"
                style={{
                  width: i === activeIndex ? 24 : 8,
                  height: 8,
                  background:
                    i === activeIndex
                      ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
                      : 'var(--border-card)',
                }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
